import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAlignLeft,
  faImage,
  faLayerGroup,
  faBuilding,
  faLightbulb,
  faPenToSquare,
  faFaceSmile,
  faHashtag,
  faWandMagicSparkles,
  faArrowRight,
} from "@fortawesome/free-solid-svg-icons";
import classNames from "classnames";
import React, { useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { Button, Col, Container, Form, Row, Spinner } from "reactstrap";
import { toast } from "react-toastify";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { OPENAI_SOCIAL_FORM } from "../../../../constants/modal.constant";
import { useGlobalCore } from "../../../../cores/globals";
import { useMeCore } from "../../../../cores/me";
import { useOpenAiCore } from "../../../../cores/openAi";
import { SocialTemplate } from "../../../../cores/openAi/interface/api/get-social-templates";
import { useSocialPostCore } from "../../../../cores/socialPost";
import { PostImage } from "../../../../cores/socialPost/interfaces/postImage";
import { generateOpenAiSocialImage } from "../../../../cores/helpers/openai-generate-social-image";
import socialApiClient from "../../../../cores/helpers/services/social-api-client";
import { buildSocialPostImagePrompt } from "./buildSocialPostImagePrompt";
import { buildOpenAiPostRequest } from "./buildOpenAiPostRequest";
import { useEmailCore } from "../../../../cores/email";
import "./OpenAiForm.scss";

type GenerationMode = "text" | "image" | "both";

interface OpenAiFormFieldProps {
  label: string;
  hint?: string;
  icon: IconDefinition;
  name: string;
  value: string;
  placeholder: string;
  required?: boolean;
  multiline?: boolean;
  onChange: (value: string) => void;
}

const OpenAiFormField = ({
  label,
  hint,
  icon,
  name,
  value,
  placeholder,
  required = false,
  multiline = false,
  onChange,
}: OpenAiFormFieldProps) => (
  <div className="openai-form-field">
    <div className="openai-form-field__header">
      <label className="openai-form-field__label" htmlFor={name}>
        {label}
      </label>
      {hint ? <span className="openai-form-field__hint">{hint}</span> : null}
    </div>
    <div className="openai-form-field__control">
      <FontAwesomeIcon icon={icon} className="openai-form-field__icon" />
      {multiline ? (
        <textarea
          id={name}
          name={name}
          className="openai-form-field__input openai-form-field__input--textarea"
          value={value}
          placeholder={placeholder}
          required={required}
          rows={3}
          onChange={(event) => onChange(event.target.value)}
        />
      ) : (
        <input
          id={name}
          name={name}
          type="text"
          className="openai-form-field__input"
          value={value}
          placeholder={placeholder}
          required={required}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
    </div>
  </div>
);

export default function OpenAiForm({
  emailTemplates,
}: {
  emailTemplates?: boolean;
}) {
  const intl = useIntl();
  const { clear, addText, addImages, socialProviders } = useSocialPostCore();
  const {
    templates,
    showTemplates,
    setShowTemplates,
    getSocialTemplate,
    getEmailTemplate,
  } = useOpenAiCore();
  const { company } = useMeCore();
  const { turnOffModal } = useGlobalCore();
  const { setMessage } = useEmailCore();
  const [selected, setSelected] = useState(0);
  const [industry, setIndustry] = useState("");
  const [subject, setSubject] = useState("");
  const [additionalInfo, setAdditionalInfo] = useState("");
  const [noEmoticons, setNoEmoticons] = useState(false);
  const [noHashtags, setNoHashtags] = useState(false);
  const [generationMode, setGenerationMode] = useState<GenerationMode>("text");
  const [generatedImage, setGeneratedImage] = useState<PostImage | null>(null);
  const [showImageResult, setShowImageResult] = useState(false);
  const [generatedTemplates, setGeneratedTemplates] = useState<
    SocialTemplate[] | null
  >(null);
  const [isGeneratingAiPost, setIsGeneratingAiPost] = useState(false);

  const mapGenerationError = useCallback((error: any) => {
    const status = error?.response?.status || error?.request?.status;
    const responseMessage =
      error?.response?.data?.message ||
      error?.response?.data?.error?.message ||
      error?.message;

    if (status === 504) {
      return "La generaci?n tard? m?s de lo esperado y el servidor cort? la espera. Intenta nuevamente en unos segundos.";
    }

    if (status === 422) {
      return "No pudimos completar esta generaci?n con esos datos. Intenta simplificar la tem?tica o la informaci?n adicional.";
    }

    if (status === 403) {
      return "OpenAI rechaz? la generaci?n. Revisa la API key en Integraciones o el saldo de tu cuenta.";
    }

    if (status === 404) {
      return "El backend a?n no tiene disponible openai/images. Hay que desplegar ese endpoint en la API social.";
    }

    return responseMessage || "No se pudo generar la publicación con IA.";
  }, []);

  const buildImagePrompt = useCallback(
    () =>
      buildSocialPostImagePrompt({
        industry,
        subject,
        additionalInfo,
        platform: "Instagram",
        brandName: company?.name,
      }),
    [additionalInfo, company?.name, industry, subject]
  );

  const generateImageForPost = useCallback(async () => {
    if (!company?.id) {
      throw new Error("No hay empresa seleccionada para generar la imagen.");
    }

    try {
      return await generateOpenAiSocialImage(
        company.id,
        buildImagePrompt(),
        "1024x1024"
      );
    } catch (error: any) {
      throw new Error(mapGenerationError(error));
    }
  }, [buildImagePrompt, company?.id, mapGenerationError]);

  const getSocialTemplatesDirect = useCallback(
    async (body: any) => {
      if (!company?.id) {
        throw new Error("No hay empresa seleccionada para generar el texto.");
      }

      try {
        const { data } = await socialApiClient().post("openai/posts", body, {
          params: { companyId: company.id },
        });
        const payload = data?.payload ?? data;
        const responseTemplates = Array.isArray(payload?.templates)
          ? payload.templates
          : payload?.template
          ? [payload.template]
          : Array.isArray(data?.templates)
          ? data.templates
          : [];

        if (!responseTemplates.length) {
          throw new Error("La IA no devolvió texto para la publicación.");
        }

        return responseTemplates as SocialTemplate[];
      } catch (error: any) {
        throw new Error(mapGenerationError(error));
      }
    },
    [company?.id, mapGenerationError]
  );

  const buildTextRequestBody = useCallback(() => {
    const socialMedias = socialProviders.map((social) => social.name!);

    return buildOpenAiPostRequest({
      industry,
      subject,
      additionalInfo,
      noEmoticons,
      noHashtags,
      socialMedias,
      contentRules: {
        noEmojis: intl.formatMessage({ id: "app.openAiForm.contentRuleNoEmojis" }),
        noHashtags: intl.formatMessage({
          id: "app.openAiForm.contentRuleNoHashtags",
        }),
      },
    });
  }, [
    additionalInfo,
    industry,
    intl,
    noEmoticons,
    noHashtags,
    socialProviders,
    subject,
  ]);

  const showContentOptions = emailTemplates || generationMode !== "image";

  const onSelecting = useCallback(
    (template) => {
      clear();
      if (emailTemplates) {
        setMessage(template.body);
      } else {
        addText(template.body);
        if (generatedImage) {
          addImages([generatedImage]);
        }
      }
      turnOffModal(OPENAI_SOCIAL_FORM);
    },
    [clear, emailTemplates, setMessage, addText, generatedImage, addImages, turnOffModal]
  );

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      if (emailTemplates) {
        getEmailTemplate(company?.id!, buildTextRequestBody());
        return;
      }

      setGeneratedImage(null);
      setGeneratedTemplates(null);
      setShowImageResult(false);
      setSelected(0);

      const requestBody = buildTextRequestBody();

      try {
        setIsGeneratingAiPost(true);

        if (generationMode === "image") {
          const image = await generateImageForPost();
          setGeneratedImage(image);
          setShowImageResult(true);
          return;
        }

        if (generationMode === "both") {
          const [image, aiTemplates] = await Promise.all([
            generateImageForPost(),
            getSocialTemplatesDirect(requestBody),
          ]);
          setGeneratedImage(image);
          setGeneratedTemplates(aiTemplates);
          return;
        }

        await getSocialTemplate(company?.id!, requestBody);
      } catch (error: any) {
        console.error("OpenAiForm AI post generation error:", error);
        toast.error(mapGenerationError(error));
      } finally {
        setIsGeneratingAiPost(false);
      }
    },
    [
      buildTextRequestBody,
      company,
      emailTemplates,
      generationMode,
      generateImageForPost,
      getEmailTemplate,
      getSocialTemplate,
      getSocialTemplatesDirect,
      mapGenerationError,
    ]
  );

  const onUsingGeneratedImage = useCallback(() => {
    if (generatedImage) {
      addImages([generatedImage]);
    }
    turnOffModal(OPENAI_SOCIAL_FORM);
  }, [generatedImage, addImages, turnOffModal]);

  const displayTemplates = useMemo(
    () => generatedTemplates ?? templates,
    [generatedTemplates, templates]
  );
  const shouldShowTemplates = showTemplates || generatedTemplates !== null;

  const template = useMemo(
    () => displayTemplates[selected] || null,
    [displayTemplates, selected]
  );

  const increase = useCallback(() => {
    if (selected < displayTemplates.length - 1) {
      setSelected(selected + 1);
    }
  }, [selected, displayTemplates]);

  const decrease = useCallback(() => {
    if (selected > 0) {
      setSelected(selected - 1);
    }
  }, [selected]);

  const back = useCallback(() => {
    setGeneratedTemplates(null);
    setGeneratedImage(null);
    setShowImageResult(false);
    setShowTemplates(false);
  }, [setShowTemplates]);

  const generationModeOptions = useMemo(
    () =>
      [
        {
          id: "text" as const,
          icon: faAlignLeft,
          labelId: "app.openAiForm.modeText",
          hintId: "app.openAiForm.modeTextHint",
        },
        {
          id: "image" as const,
          icon: faImage,
          labelId: "app.openAiForm.modeImage",
          hintId: "app.openAiForm.modeImageHint",
        },
        {
          id: "both" as const,
          icon: faLayerGroup,
          labelId: "app.openAiForm.modeBoth",
          hintId: "app.openAiForm.modeBothHint",
        },
      ] as const,
    []
  );

  const canGenerate = industry.trim().length > 0 && subject.trim().length > 0;

  const generateButtonContent = useMemo(() => {
    if (emailTemplates) {
      return {
        title: isGeneratingAiPost
          ? intl.formatMessage({ id: "app.openAiForm.generatingText" })
          : intl.formatMessage({ id: "app.openAiForm.generateAction" }),
        subtitle: intl.formatMessage({ id: "app.openAiForm.generateSubtitleEmail" }),
      };
    }

    if (isGeneratingAiPost) {
      switch (generationMode) {
        case "image":
          return {
            title: intl.formatMessage({ id: "app.openAiForm.generatingImage" }),
            subtitle: "",
          };
        case "both":
          return {
            title: intl.formatMessage({ id: "app.openAiForm.generatingBoth" }),
            subtitle: "",
          };
        default:
          return {
            title: intl.formatMessage({ id: "app.openAiForm.generatingText" }),
            subtitle: "",
          };
      }
    }

    switch (generationMode) {
      case "image":
        return {
          title: intl.formatMessage({ id: "app.openAiForm.generateAction" }),
          subtitle: intl.formatMessage({
            id: "app.openAiForm.generateSubtitleImage",
          }),
        };
      case "both":
        return {
          title: intl.formatMessage({ id: "app.openAiForm.generateAction" }),
          subtitle: intl.formatMessage({
            id: "app.openAiForm.generateSubtitleBoth",
          }),
        };
      default:
        return {
          title: intl.formatMessage({ id: "app.openAiForm.generateAction" }),
          subtitle: intl.formatMessage({
            id: "app.openAiForm.generateSubtitleText",
          }),
        };
    }
  }, [emailTemplates, generationMode, intl, isGeneratingAiPost]);

  const generationStatusMessage = useMemo(() => {
    switch (generationMode) {
      case "image":
        return intl.formatMessage({ id: "app.openAiForm.generatingImageStatus" });
      case "both":
        return intl.formatMessage({ id: "app.openAiForm.generatingBothStatus" });
      default:
        return intl.formatMessage({ id: "app.openAiForm.generatingTextStatus" });
    }
  }, [generationMode, intl]);

  const next = useCallback(() => {
    setShowTemplates(true);
  }, [setShowTemplates]);

  return (
    <Container className="h-100 openai-form" fluid>
      <Row className="h-100">
        <Col xs="12">
          <div
            className={classNames("d-flex flex-column h-100 p-3", {
              "justify-content-between":
                shouldShowTemplates || showImageResult,
            })}
          >
            <div className="form-header">
              <h2 className="h2 d-flex justify-content-center align-items-center mb-4">
                {showImageResult
                  ? intl.formatMessage({ id: "app.openAiForm.generatedImageTitle" })
                  : shouldShowTemplates
                  ? intl.formatMessage({ id: "app.socialStory.template" })
                  : intl.formatMessage({ id: "app.openAiForm.title" })}
              </h2>
            </div>

            {showImageResult && generatedImage ? (
              <>
                <div className="template-section">
                  <div className="template-content image-only-result">
                    <div className="d-flex flex-column align-items-center w-100">
                      <p className="text-muted mb-3">
                        {intl.formatMessage({ id: "app.openAiForm.imageReady" })}
                      </p>
                      <div className="template-image-preview">
                        <img
                          src={generatedImage.data}
                          alt={intl.formatMessage({
                            id: "app.openAiForm.generatedImageAlt",
                          })}
                        />
                      </div>
                      <Button
                        color="primary"
                        className="mt-3"
                        onClick={onUsingGeneratedImage}
                      >
                        {intl.formatMessage({ id: "app.openAiForm.useImage" })}
                      </Button>
                    </div>
                  </div>
                </div>

                <Button
                  className="my-1 mt-3"
                  color="white"
                  block
                  onClick={back}
                >
                  {intl.formatMessage({ id: "app.openAiForm.regenerate" })}
                </Button>
              </>
            ) : shouldShowTemplates ? (
              <>
                <div className="template-section">
                  <div className="template-navigation">
                    <button
                      className="nav-button"
                      onClick={decrease}
                      disabled={selected === 0}
                    >
                      <FontAwesomeIcon icon={["fas", "angle-left"]} size="lg" />
                    </button>

                    <div className="template-content">
                      {template ? (
                        <div className="d-flex flex-column align-items-center">
                          <div className="template-text pre-wrap">{template.body}</div>

                          {generatedImage && (
                            <div className="template-image-preview mt-3">
                              <img
                                src={generatedImage.data}
                                alt="Publicación generada con IA"
                              />
                            </div>
                          )}

                          <div className="template-counter text-muted mt-3">
                            {selected + 1} / {displayTemplates.length}
                          </div>

                          <Button
                            color="primary"
                            className="mt-3"
                            onClick={() => onSelecting(template)}
                          >
                            {intl.formatMessage({
                              id: "app.socialStory.template.select",
                            })}
                          </Button>
                        </div>
                      ) : (
                        <label className="text-muted">
                          {intl.formatMessage({ id: "app.socialStory.template.empty" })}
                        </label>
                      )}
                    </div>

                    <button
                      className="nav-button"
                      onClick={increase}
                      disabled={selected === displayTemplates.length - 1}
                    >
                      <FontAwesomeIcon icon={["fas", "angle-right"]} size="lg" />
                    </button>
                  </div>
                </div>

                <Button
                  className="my-1 mt-3"
                  color="white"
                  block
                  onClick={back}
                >
                  {intl.formatMessage({ id: "app.openAiForm.regenerate" })}
                </Button>
              </>
            ) : (
              <div className="d-flex flex-column form-section">
                <Form onSubmit={handleSubmit}>
                  <div className="openai-form-fields">
                    <OpenAiFormField
                      label={intl.formatMessage({ id: "app.openAiForm.industry" })}
                      hint={intl.formatMessage({ id: "app.openAiForm.industryHint" })}
                      icon={faBuilding}
                      name="industry"
                      value={industry}
                      placeholder={intl.formatMessage({
                        id: "app.openAiForm.industryPlaceholder",
                      })}
                      required
                      onChange={setIndustry}
                    />

                    <OpenAiFormField
                      label={intl.formatMessage({
                        id: emailTemplates
                          ? "app.openAiForm.purpose"
                          : "app.openAiForm.topic",
                      })}
                      hint={intl.formatMessage({ id: "app.openAiForm.topicHint" })}
                      icon={faLightbulb}
                      name="subject"
                      value={subject}
                      placeholder={intl.formatMessage({
                        id: "app.openAiForm.topicPlaceholder",
                      })}
                      required
                      onChange={setSubject}
                    />

                    <OpenAiFormField
                      label={intl.formatMessage({
                        id: "app.openAiForm.additionalInfo",
                      })}
                      hint={intl.formatMessage({
                        id: "app.openAiForm.additionalInfoHint",
                      })}
                      icon={faPenToSquare}
                      name="additional"
                      value={additionalInfo}
                      placeholder={intl.formatMessage({
                        id: "app.openAiForm.additionalInfoPlaceholder",
                      })}
                      multiline
                      onChange={setAdditionalInfo}
                    />
                  </div>

                  {!emailTemplates && (
                    <div className="generation-mode-section">
                      <p className="generation-mode-section__title">
                        {intl.formatMessage({ id: "app.openAiForm.modeTitle" })}
                      </p>
                      <div className="generation-mode-options">
                        {generationModeOptions.map((option) => (
                          <button
                            key={option.id}
                            type="button"
                            className={classNames("generation-mode-option", {
                              "generation-mode-option--active":
                                generationMode === option.id,
                            })}
                            onClick={() => setGenerationMode(option.id)}
                            aria-pressed={generationMode === option.id}
                          >
                            <span className="generation-mode-option__icon">
                              <FontAwesomeIcon icon={option.icon} />
                            </span>
                            <span className="generation-mode-option__label">
                              {intl.formatMessage({ id: option.labelId })}
                            </span>
                            <span className="generation-mode-option__hint">
                              {intl.formatMessage({ id: option.hintId })}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {showContentOptions && (
                    <div className="content-options-section">
                      <p className="content-options-section__title">
                        {intl.formatMessage({
                          id: "app.openAiForm.contentOptionsTitle",
                        })}
                      </p>
                      <div className="content-option-toggles">
                        <button
                          type="button"
                          className={classNames("content-option-toggle", {
                            "content-option-toggle--active": noEmoticons,
                          })}
                          onClick={() => setNoEmoticons((current) => !current)}
                          aria-pressed={noEmoticons}
                        >
                          <FontAwesomeIcon icon={faFaceSmile} />
                          {intl.formatMessage({ id: "app.openAiForm.noEmoticons" })}
                        </button>
                        <button
                          type="button"
                          className={classNames("content-option-toggle", {
                            "content-option-toggle--active": noHashtags,
                          })}
                          onClick={() => setNoHashtags((current) => !current)}
                          aria-pressed={noHashtags}
                        >
                          <FontAwesomeIcon icon={faHashtag} />
                          {intl.formatMessage({ id: "app.openAiForm.noHashtags" })}
                        </button>
                      </div>
                    </div>
                  )}

                  {isGeneratingAiPost && (
                    <div className="openai-generation-status" role="status">
                      <div className="openai-generation-status__animation">
                        <span />
                        <span />
                        <span />
                      </div>
                      <div>
                        <strong>{generationStatusMessage}</strong>
                        <p>
                          {intl.formatMessage({
                            id: "app.openAiForm.generatingWait",
                          })}
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="button-section">
                    {displayTemplates.length > 0 && (
                      <Button
                        className="openai-secondary-btn my-1 mt-4"
                        color="white"
                        block
                        onClick={next}
                      >
                        {intl.formatMessage({ id: "app.openAiForm.viewLastGenerated" })}
                      </Button>
                    )}
                    <button
                      type="submit"
                      className={classNames("openai-generate-btn", {
                        "openai-generate-btn--loading": isGeneratingAiPost,
                      })}
                      disabled={isGeneratingAiPost || !canGenerate}
                    >
                      <span className="openai-generate-btn__icon" aria-hidden="true">
                        {isGeneratingAiPost ? (
                          <Spinner size="sm" color="light" />
                        ) : (
                          <FontAwesomeIcon icon={faWandMagicSparkles} />
                        )}
                      </span>
                      <span className="openai-generate-btn__content">
                        <span className="openai-generate-btn__title">
                          {generateButtonContent.title}
                        </span>
                        {generateButtonContent.subtitle ? (
                          <span className="openai-generate-btn__subtitle">
                            {generateButtonContent.subtitle}
                          </span>
                        ) : null}
                      </span>
                      {!isGeneratingAiPost && (
                        <FontAwesomeIcon
                          icon={faArrowRight}
                          className="openai-generate-btn__arrow"
                          aria-hidden="true"
                        />
                      )}
                    </button>
                    {!canGenerate && !isGeneratingAiPost && (
                      <p className="openai-generate-btn__hint">
                        {intl.formatMessage({ id: "app.openAiForm.generateHint" })}
                      </p>
                    )}
                  </div>
                </Form>
              </div>
            )}
          </div>
        </Col>
      </Row>
    </Container>
  );
}
